import { Component, ErrorInfo, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { Container } from "../../styledComponents/Container/Container";
import Button from "../../styledComponents/CustomButtons/Buttons";
import { COLORS } from "../../constants/colors/colors";
import { styled } from "styled-components";
import { isUserAuthenticated } from "../../utils/authUtils";

const ErrorPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  gap: 1.5rem;
  text-align: center;
`;

const ErrorTitle = styled.h1`
  margin: 0;
  color: ${COLORS.IMMERSIVE_BLUE_DARK};
  font-size: 1.75rem;
  font-weight: 700;
`;

const ErrorDescription = styled.p`
  margin: 0;
  max-width: 28rem;
  color: ${COLORS.IMMERSIVE_BLUE_DARK};
  font-size: 1rem;
  line-height: 1.5;
`;

interface AppErrorFallbackProps {
  onReload: () => void;
}

const AppErrorFallback = ({
  onReload,
}: AppErrorFallbackProps): JSX.Element => {
  const { t } = useTranslation("common");

  const handleGoHome = () => {
    window.location.href = isUserAuthenticated() ? "/home" : "/login";
  };

  return (
    <ErrorPanel role="alert">
      <ErrorTitle>{t("appErrorTitle")}</ErrorTitle>
      <ErrorDescription>{t("appErrorDescription")}</ErrorDescription>
      <Container flexDirection="row" gap="1rem" justifyContent="center">
        <Button type="button" variant="primary" onClick={onReload}>
          {t("appErrorReload")}
        </Button>
        <Button type="button" variant="secondary" onClick={handleGoHome}>
          {t("appErrorGoHome")}
        </Button>
      </Container>
    </ErrorPanel>
  );
};

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    if (error?.name === "ChunkLoadError") {
      return { hasError: false };
    }

    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (error?.name === "ChunkLoadError") {
      const hasReloaded = sessionStorage.getItem("chunk-reload");

      if (!hasReloaded) {
        sessionStorage.setItem("chunk-reload", "true");
        window.location.reload();
        return;
      }

      this.setState({ hasError: true });
      return;
    }

    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return <AppErrorFallback onReload={this.handleReload} />;
    }

    return this.props.children;
  }
}
